import type {WorkstreamBasicWithSummary} from 'client';
import {useMemo} from 'react';
import {useWorkstreams} from 'views/workstreams/detail/hooks';
import type {
  AltitudeFilterValue,
  StatusValue,
  TimeframeFilter,
  WorkstreamFilters,
} from 'views/workstreams/detail/types';

import {myOverdueOutcomes} from './outcomesOverdue';
import {isUpdateDueForMe} from './updateDue';

const myCardFilters = (timeframe: TimeframeFilter): WorkstreamFilters => ({
  altitude: ['false'] as AltitudeFilterValue[],
  timeframes: [timeframe],
  health: ['archived' as StatusValue],
});

/**
 * The rail's My workstreams badge: My's Update due cards (status updates you
 * owe) plus its Outcomes overdue cards, in the half and Enablement, so it's
 * the two sections' badges added up. The same requests as My's unfiltered
 * cards, so on My they come from its cache.
 */
export const useMyWorkstreamsBadgeCount = ({
  timeframe,
  roleOf,
  myLdap,
}: {
  timeframe: TimeframeFilter;
  roleOf: (id: number) => string | null | undefined;
  myLdap: string | null | undefined;
}): number => {
  const halfFilters = useMemo(() => myCardFilters(timeframe), [timeframe]);
  const enablementFilters = useMemo(() => myCardFilters('enablement'), []);
  const half = useWorkstreams({
    scope_type: 'my_workstreams',
    scope_value: null,
    filters: halfFilters,
    group_by: 'none',
    autoFetchAllPages: true,
  });
  const enablement = useWorkstreams({
    scope_type: 'my_workstreams',
    scope_value: null,
    filters: enablementFilters,
    group_by: 'none',
    autoFetchAllPages: true,
  });
  const rows = [
    ...((half.data?.workstreams ?? []) as WorkstreamBasicWithSummary[]),
    ...((enablement.data?.workstreams ?? []) as WorkstreamBasicWithSummary[]),
  ];
  // A workstream in both lists counts once, and one owing an update counts
  // only there (as on the page).
  const due = new Set(
    rows.filter((row) => isUpdateDueForMe(row, roleOf(row.id))).map((row) => row.id)
  );
  const outcomesOverdue = new Set(
    rows
      .filter((row) => !due.has(row.id) && myOverdueOutcomes(row, myLdap).length > 0)
      .map((row) => row.id)
  );
  return due.size + outcomesOverdue.size;
};
